import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AudioDeviceService } from '../../services/stt-chunked/audio-device/audio-device.service';
import { AudioStreamService } from '../../services/stt-chunked/audio-stream/audio-stream.service';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { MESSAGES } from '../../util/constants';

@Component({
  standalone: false,
  selector: 'cva-microphone-selector',
  templateUrl: './microphone-selector.component.html',
  styleUrl: './microphone-selector.component.scss',
})
export class MicrophoneSelectorComponent implements OnInit, OnDestroy {
  @Input() telehealthActive = false;

  readonly uid = `mic-${Math.random().toString(36).slice(2, 8)}`;
  readonly telehealthLabel = 'Telehealth (Screen Share)';

  audioInputDevices: MediaDeviceInfo[] = [];
  showDropdown = false;
  selectedDeviceId = '';
  volumeLevel = 0;

  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array<ArrayBuffer> | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private animationId = 0;
  private deviceChangeListener: (() => void) | null = null;
  private streamSub: Subscription | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly audioDeviceService: AudioDeviceService,
    private readonly audioStreamService: AudioStreamService,
    private readonly cvaNotificationService: CVANotificationService,
  ) { }

  get hasDevices(): boolean {
    return this.audioInputDevices.length > 0;
  }

  get hasActiveTelehealthAudio(): boolean {
    return this.audioStreamService.hasActiveTelehealthAudio;
  }

  get showTelehealthSource(): boolean {
    return this.telehealthActive || this.audioStreamService.audioSource === 'telehealth';
  }

  get scaledVolumeLevel(): number {
    const max = 100;
    const sensitivity = 1.2;
    return Math.min(max, Math.pow(this.volumeLevel / max, sensitivity) * max);
  }

  async ngOnInit(): Promise<void> {
    this.audioCtx = new AudioContext();
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 64;
    this.analyser.smoothingTimeConstant = 0.8;

    try {
      const permission = await navigator.mediaDevices.getUserMedia({ audio: true });
      permission.getTracks().forEach(track => track.stop());
      this.audioInputDevices = await this.audioDeviceService.getMicrophones();
      const saved = this.audioDeviceService.getDeviceId() || this.audioInputDevices[0]?.deviceId;
      if (saved) {
        this.selectedDeviceId = saved;
        this.audioDeviceService.setDeviceId(saved);
      }
    } catch (error) {
      this.audioInputDevices = [];
      console.error('Error initializing mic:', error);
    }

    this.deviceChangeListener = async () => {
      try {
        this.audioInputDevices = await this.audioDeviceService.getMicrophones();
      } catch (error) {
        console.error('Failed to refresh microphones', error);
      }
    };
    navigator.mediaDevices?.addEventListener('devicechange', this.deviceChangeListener);

    this.streamSub = this.audioStreamService.stream$.subscribe(stream => {
      if (stream) {
        this.bindSharedStream(stream);
      } else {
        this.stopAnalyser();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showDropdown) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.showDropdown = false;
    }
  }

  toggleDropdown(event?: MouseEvent): void {
    event?.stopPropagation();
    this.showDropdown = !this.showDropdown;
  }

  async selectDevice(deviceId: string, isManualChange = true): Promise<void> {
    try {
      if (this.audioCtx?.state === 'suspended') {
        await this.audioCtx.resume();
      }

      this.selectedDeviceId = deviceId;
      this.audioDeviceService.setDeviceId(deviceId);

      if (isManualChange) {
        this.cvaNotificationService.showNotification(
          MESSAGES.MIC_DEVICE_SELECTED,
          NotificationClassEnum.SUCCESS,
        );
      }

      this.showDropdown = false;
    } catch (error) {
      console.error('Error selecting device:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.deviceChangeListener) {
      navigator.mediaDevices?.removeEventListener('devicechange', this.deviceChangeListener);
      this.deviceChangeListener = null;
    }
    this.streamSub?.unsubscribe();
    this.stopAnalyser();
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      this.audioCtx.close().catch(() => undefined);
    }
  }

  private bindSharedStream(stream: MediaStream): void {
    this.stopAnalyser();
    this.connectAnalyser(stream);
  }

  private connectAnalyser(stream: MediaStream): void {
    if (!this.audioCtx || !this.analyser) return;
    this.sourceNode = this.audioCtx.createMediaStreamSource(stream);
    this.sourceNode.connect(this.analyser);
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    cancelAnimationFrame(this.animationId);
    this.animate();
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);
    if (!this.analyser || !this.dataArray) return;
    this.analyser.getByteFrequencyData(this.dataArray);
    const average = this.dataArray.reduce((sum, val) => sum + val, 0) / this.dataArray.length;
    this.volumeLevel = Math.min(100, Math.round((average / 50) * 40));
  };

  private stopAnalyser(): void {
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch {
        // already disconnected
      }
      this.sourceNode = null;
    }
    cancelAnimationFrame(this.animationId);
    this.volumeLevel = 0;
  }
}
